import { useMutation, useQueryClient } from '@tanstack/react-query'
import { JsonRpcError } from '~/types'
import { WithdrawRequest, WithdrawRequestListResponse } from '../types'
import { keepStaffContext } from './keepStaffContext'

/**
 * Decline a withdrawal without refunding it.
 *
 * The sibling of useWithdrawDecline. The backend has had
 * `bank.withdraw_request.decline_hold` since the funds_held field was added --
 * the debit survives, so the money is neither paid out nor returned while an
 * investigation runs. It had no caller, which meant every decline in the UI
 * silently handed the balance back, spendable in the store.
 *
 * Deliberately a separate endpoint rather than a flag: refunding or not is a
 * decision somebody should make on purpose, per request.
 */
export const useWithdrawDeclineHold = () => {
    const queryClient = useQueryClient()

    return useMutation<WithdrawRequest, JsonRpcError, unknown>({
        mutationFn: async ({ uuid, comment }) => {
            const resp = await fetch('/rpc', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    jsonrpc: '2.0',
                    id: 1,
                    method: 'bank.withdraw_request.decline_hold',
                    params: {
                        uuid,
                        comment,
                    },
                }),
            })
            const data = await resp.json()

            if (data.error) {
                throw data
            } else {
                return data.result
            }
        },
        onSuccess: (data) => {
            queryClient.setQueriesData(['withdraw_requests'], (prev: WithdrawRequestListResponse) => {
                return {
                    ...prev,
                    data: prev.data?.map((item) =>
                        item.uuid === data.uuid
                            ? keepStaffContext(item, data)
                            : // Held by the cascade too. Without this the sibling
                              // kept offering Approve and Decline, and a Refund on
                              // it failed with a bare "Server Error".
                              data.also_declined?.includes(item.uuid)
                              ? { ...item, status: 'declined' }
                              : item
                    ),
                }
            })
            if (data.also_declined?.length) queryClient.invalidateQueries(['withdraw_requests'])
            queryClient.invalidateQueries(['withdraw_request.summary'])
            // The verdict and block pills are cached for good (`staleTime:
            // Infinity`); an action is exactly when they may have moved.
            queryClient.invalidateQueries(['withdraw_verdicts'])
            queryClient.invalidateQueries(['payout_wallet_balance'])
        },
        retry: false,
    })
}
